import { useMemo } from "react";

import {
  useCountDocumentJobs,
  useListCareProtocolOpportunities,
} from "@elation/api-client/src/stitch-web";
import type { CareProtocolOpportunityListItem } from "@elation/api-client/src/stitch-web/models";

import {
  isStaleCursorError,
  shouldRetryCursorQueryError,
  useCursorPagination,
  useResetStaleCursor,
} from "@src/shared/hooks/useCursorPagination";

import { ActiveUploadProcessingGroup } from "./ActiveUploadProcessingGroup";
import { CareProtocolOpportunityListSkeleton } from "./CareProtocolOpportunityListSkeleton";
import { CareProtocolOpportunityUploadGroup } from "./CareProtocolOpportunityUploadGroup";
import { EmptyCareProtocolOpportunities } from "./EmptyCareProtocolOpportunities";
import { ErrorCareProtocolOpportunities } from "./ErrorCareProtocolOpportunities";
import { PaginationControls } from "./PaginationControls";

type OpportunityDateGroup = {
  date: string;
  uploadedAt: string;
  items: CareProtocolOpportunityListItem[];
};

function groupByOpportunityDate(
  items: CareProtocolOpportunityListItem[],
): OpportunityDateGroup[] {
  const groups = new Map<string, OpportunityDateGroup>();

  for (const item of items) {
    const date = item.createdAt.slice(0, 10);
    const group = groups.get(date) ?? {
      date,
      uploadedAt: item.createdAt,
      items: [],
    };
    group.items.push(item);
    groups.set(date, group);
  }

  return [...groups.values()];
}

export function ExtractedCareGapsTab() {
  const {
    currentCursor,
    goToNextPage,
    goToPreviousPage,
    hasPrevPage,
    resetCursor,
  } = useCursorPagination();
  const query = useListCareProtocolOpportunities(
    {
      cursor: currentCursor,
      windowSize: 5,
      windowUnit: "days",
    },
    {
      query: {
        refetchInterval: currentCursor ? false : 5_000,
        retry: shouldRetryCursorQueryError,
      },
    },
  );
  const documentJobsCountQuery = useCountDocumentJobs({
    query: {
      enabled: !currentCursor,
      refetchInterval: 5_000,
    },
  });
  const items = query.data?.data.items ?? [];
  const uploadGroups = useMemo(() => groupByOpportunityDate(items), [items]);
  const activeUploadCount = documentJobsCountQuery.data?.data.count ?? 0;
  const nextCursor = query.data?.data.nextCursor ?? null;
  const isStaleCursor = isStaleCursorError({
    currentCursor,
    error: query.error,
    isError: query.isError,
  });
  const hasNextPage = Boolean(nextCursor);

  useResetStaleCursor(isStaleCursor, resetCursor);

  const pagination = (
    <PaginationControls
      hasNextPage={hasNextPage}
      hasPrevPage={hasPrevPage}
      onPrevious={goToPreviousPage}
      onNext={() => goToNextPage(nextCursor)}
    />
  );

  if (query.isLoading || isStaleCursor) {
    return <CareProtocolOpportunityListSkeleton />;
  }

  if (query.isError && !isStaleCursor) {
    return <ErrorCareProtocolOpportunities />;
  }

  if (uploadGroups.length === 0 && activeUploadCount === 0) {
    return (
      <>
        <EmptyCareProtocolOpportunities />
        {pagination}
      </>
    );
  }

  return (
    <>
      <ul aria-label="Care protocol opportunities" className="space-y-3">
        <ActiveUploadProcessingGroup count={activeUploadCount} />
        {uploadGroups.map((group, index) => (
          <CareProtocolOpportunityUploadGroup
            key={group.date}
            defaultOpen={index === 0}
            date={group.date}
            items={group.items}
            uploadedAt={group.uploadedAt}
          />
        ))}
      </ul>
      {pagination}
    </>
  );
}
